import { Placement } from '@floating-ui/react';
import { HabitSchedule } from '@griply/types';
import { getScheduleLabel, groupSchedulesByTime } from '@griply/utils';
import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';

export type HabitSchedulesMenuProps = {
  schedules: HabitSchedule[];
  referenceElement: React.RefObject<HTMLElement>;
  onClose: () => void;
  onSelect: (schedule: HabitSchedule) => void;
  position?: Placement;
};

export const HabitSchedulesMenu: React.FC<HabitSchedulesMenuProps> = ({
  schedules,
  referenceElement,
  onClose,
  onSelect,
  position = 'bottom-start',
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const { current, upcoming, history } = useMemo(
    () => groupSchedulesByTime(schedules, today),
    [schedules, today],
  );

  const renderSchedule = (schedule: HabitSchedule) => {
    const label = getScheduleLabel(schedule, t, true);

    // Format: "12 Jan 2024" or "12 Jan - 15 Feb 2024"
    const startStr = format(schedule.startDate, 'd MMM yyyy');
    const endStr = schedule.endDate
      ? format(schedule.endDate, 'd MMM yyyy')
      : null;
    const dateLabel = endStr
      ? `${startStr} - ${endStr}`
      : `${startStr} - ${t('general.labels.habit.repeat.options.no-end-date.label')}`;

    return (
      <PopupMenuListItem key={schedule.startDate.toISOString()}>
        <PopupMenuButton
          onClick={() => onSelect(schedule)}
          start={<Icon icon={Repeat} />}
          subtitle={dateLabel}
        >
          {label}
        </PopupMenuButton>
      </PopupMenuListItem>
    );
  };

  return (
    <PopupMenu
      referenceElement={referenceElement}
      onOutside={onClose}
      position={position}
    >
      {!!current.length && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuTitle
            title={t('habit.schedule.sections.current')}
            isSubTitle
          />
          {current.map(renderSchedule)}
        </PopupMenuList>
      )}

      {!!upcoming.length && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuTitle
            title={t('habit.schedule.sections.upcoming')}
            isSubTitle
          />
          {upcoming.map(renderSchedule)}
        </PopupMenuList>
      )}

      {!!history.length && (
        <PopupMenuList>
          <PopupMenuTitle
            title={t('habit.schedule.sections.history')}
            isSubTitle
          />
          {history.map(renderSchedule)}
        </PopupMenuList>
      )}
    </PopupMenu>
  );
};
